import { useEffect, useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Divider,
  Grid,
  Link,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCartOutlined';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';

import quotesApi from '../../api/quotesApi';
import useCart from '../../cart/useCart';
import { CONTACT_WHATSAPP_NUMBER } from '../../config';
import ProductCard, { OFFER_LABELS, formatMoney } from '../../sections/client/products/ProductCard';
import ProductCarousel from '../../sections/client/products/ProductCarousel';
import ProductImagePlaceholder from '../../sections/client/products/ProductImagePlaceholder';
import QuantityStepper from '../../sections/client/products/QuantityStepper';

export default function ProductDetailPage() {
  const { id } = useParams();
  const { addItem, openDrawer } = useCart();

  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const [selectedImageId, setSelectedImageId] = useState(null);
  const [currency, setCurrency] = useState('PEN');
  const [quantity, setQuantity] = useState(1);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(false);
    setProduct(null);
    setQuantity(1);

    quotesApi
      .getCatalogProduct(id)
      .then(({ data }) => {
        if (!active) return;
        setProduct(data.data);
        setSelectedImageId(data.data.images?.[0]?.id ?? null);
      })
      .catch(() => {
        if (active) setError(true);
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [id]);

  // "Tambien te puede interesar": otros productos activos de la misma categoria.
  useEffect(() => {
    if (!product?.category?.id) {
      setRelated([]);
      return;
    }
    quotesApi
      .getCatalogProducts({ category_id: product.category.id, exclude: product.id, limit: 8 })
      .then(({ data }) => setRelated(data.data))
      .catch(() => setRelated([]));
  }, [product]);

  const handleAddToCart = (targetProduct, targetQuantity) => {
    addItem(targetProduct, targetQuantity);
    openDrawer();
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 5 }}>
        <Grid container spacing={4}>
          <Grid size={{ xs: 12, md: 5 }}>
            <Skeleton variant="rounded" height={360} />
          </Grid>
          <Grid size={{ xs: 12, md: 7 }}>
            <Skeleton variant="text" height={48} width="80%" />
            <Skeleton variant="text" height={32} width="40%" />
            <Skeleton variant="rounded" height={120} sx={{ mt: 2 }} />
          </Grid>
        </Grid>
      </Container>
    );
  }

  if (error || !product) {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Alert severity="error">No encontramos este producto. Puede que ya no esté disponible.</Alert>
      </Container>
    );
  }

  const priceField = currency === 'USD' ? 'price_usd' : 'price_pen';
  const regularPrice = product[currency === 'USD' ? 'price_usd_regular' : 'price_pen_regular'];
  const offer = OFFER_LABELS[product.offer_type];
  const outOfStock = product.stock_quantity <= 0;
  const selectedImage = product.images.find((image) => image.id === selectedImageId) || product.images[0];
  const whatsAppMessage = `Hola, quisiera consultar por el producto "${product.name}".`;

  return (
    <Container maxWidth="lg" sx={{ py: 5 }}>
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link component={RouterLink} to="/" underline="hover" color="inherit">
          Inicio
        </Link>
        <Link component={RouterLink} to="/catalogo" underline="hover" color="inherit">
          Catálogo
        </Link>
        {product.category?.name && <Typography color="text.secondary">{product.category.name}</Typography>}
        <Typography color="text.primary">{product.name}</Typography>
      </Breadcrumbs>

      <Grid container spacing={4}>
        {/* Galeria: miniaturas + imagen principal */}
        <Grid size={{ xs: 12, md: 5 }}>
          <Stack direction="row" spacing={1.5}>
            {product.images.length > 1 && (
              <Stack spacing={1}>
                {product.images.map((image) => (
                  <Box
                    key={image.id}
                    component="img"
                    src={image.url}
                    alt=""
                    onClick={() => setSelectedImageId(image.id)}
                    sx={{
                      width: 56,
                      height: 56,
                      objectFit: 'contain',
                      cursor: 'pointer',
                      border: '2px solid',
                      borderColor: image.id === selectedImage?.id ? 'primary.main' : 'divider',
                      borderRadius: 1,
                      bgcolor: 'background.paper',
                    }}
                  />
                ))}
              </Stack>
            )}

            <Box sx={{ flexGrow: 1 }}>
              {selectedImage ? (
                <Box
                  component="img"
                  src={selectedImage.url}
                  alt={product.name}
                  sx={{ width: '100%', height: 360, objectFit: 'contain', bgcolor: 'background.paper' }}
                />
              ) : (
                <ProductImagePlaceholder height={360} iconFontSize={80} />
              )}
            </Box>
          </Stack>
        </Grid>

        {/* Datos, precio y compra */}
        <Grid size={{ xs: 12, md: 7 }}>
          <Stack spacing={1.5}>
            {product.brand?.name && (
              <Typography variant="overline" color="text.secondary">
                {product.brand.name}
              </Typography>
            )}
            <Typography variant="h4">{product.name}</Typography>

            {offer && (
              <Chip size="small" color={offer.color} label={offer.label} sx={{ alignSelf: 'flex-start', fontWeight: 700 }} />
            )}

            <Stack sx={{ alignItems: 'center', flexWrap: 'wrap' }} direction="row" spacing={2}>
              <Typography variant="h4" color={offer ? `${offer.color}.main` : 'primary.main'}>
                {formatMoney(product[priceField], currency)}
              </Typography>
              {regularPrice != null && (
                <Typography variant="h6" color="text.secondary" sx={{ textDecoration: 'line-through' }}>
                  {formatMoney(regularPrice, currency)}
                </Typography>
              )}
              <ToggleButtonGroup
                size="small"
                exclusive
                value={currency}
                onChange={(e, value) => value && setCurrency(value)}
              >
                <ToggleButton value="PEN">Soles</ToggleButton>
                <ToggleButton value="USD">Dólares</ToggleButton>
              </ToggleButtonGroup>
            </Stack>

            <Chip
              size="small"
              sx={{ alignSelf: 'flex-start' }}
              color={outOfStock ? 'default' : 'success'}
              label={outOfStock ? 'Sin stock' : `Stock disponible: ${product.stock_quantity}`}
            />

            <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', mt: 1 }}>
              <QuantityStepper value={quantity} onChange={setQuantity} disabled={outOfStock} />

              <Button
                variant="contained"
                size="large"
                startIcon={<AddShoppingCartIcon />}
                disabled={outOfStock}
                onClick={() => handleAddToCart(product, quantity)}
                sx={{ flexGrow: 1 }}
              >
                Agregar al carrito
              </Button>
            </Stack>

            <Button
              variant="outlined"
              color="success"
              startIcon={<WhatsAppIcon />}
              component="a"
              href={`https://web.whatsapp.com/send?phone=${CONTACT_WHATSAPP_NUMBER}&text=${encodeURIComponent(whatsAppMessage)}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              Comprar por WhatsApp
            </Button>
          </Stack>
        </Grid>
      </Grid>

      {product.description && (
        <Card sx={{ mt: 5 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Descripción
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-line' }}>
              {product.description}
            </Typography>
          </CardContent>
        </Card>
      )}

      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Características del producto
          </Typography>
          <Table size="small">
            <TableBody>
              <TableRow>
                <TableCell sx={{ color: 'text.secondary', width: 200 }}>Marca</TableCell>
                <TableCell>{product.brand?.name || '—'}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ color: 'text.secondary' }}>Categoría</TableCell>
                <TableCell>{product.category?.name || '—'}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {related.length > 0 && (
        <Box sx={{ mt: 5 }}>
          <Divider sx={{ mb: 3 }} />
          <Typography variant="h5" sx={{ mb: 3 }}>
            También te puede interesar
          </Typography>
          <ProductCarousel>
            {related.map((item) => (
              <ProductCard key={item.id} product={item} currency={currency} onAddToCart={handleAddToCart} />
            ))}
          </ProductCarousel>
        </Box>
      )}
    </Container>
  );
}
